import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { StyledSelect } from "../../bugherdComponents/styled";
import { useLazyGetSprintByIdQuery } from "@/features/bughurd/api";
import {
  selectActiveSprintIds,
  selectActiveSprints,
} from "@/features/bughurd/boardSelector";
import { useAppSelector } from "@/app/hooks";

interface SprintCompleteModalProps {
  open: boolean;
  onClose: () => void;
  onComplete: (data: SprintCompleteData) => void;
  sprint?: string;
  draftSprints: Array<{
    _id: string;
    name: string;
    status: string;
  }>;
  isLoading?: boolean;
}

interface SprintCompleteData {
  sprintId: string;
  moveOpenItemsTo: string; // "new_sprint", "backlog", or sprint ID
}

const SprintCompleteModal: React.FC<SprintCompleteModalProps> = ({
  open,
  onClose,
  onComplete,
  sprint,
  draftSprints,
  isLoading = false,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const activeSprints = useAppSelector(selectActiveSprints);

  const [moveToOption, setMoveToOption] = useState<string>("new_sprint");
  const [selectedSprintId, setSelectedSprintId] = useState<string>("");

  // Use lazy query hook - this returns a trigger function
  const [
    triggerGetSprint,
    { data: sprintDetails, isLoading: isLoadingSprintDetails },
  ] = useLazyGetSprintByIdQuery();

  // Determine which sprint to use
  const getSprintToComplete = () => {
    if (sprint) {
      return sprint;
    }

    if (activeSprints && activeSprints.length === 1) {
      return activeSprints[0]._id;
    }

    return selectedSprintId;
  };

  const sprintToComplete = getSprintToComplete();

  // Fetch sprint details whenever sprintToComplete changes
  useEffect(() => {
    if (open && sprintToComplete) {
      // Trigger the query - this will always make a fresh API call
      triggerGetSprint({ sprintId: sprintToComplete }, false);
    }
  }, [open, sprintToComplete, triggerGetSprint]);

  const sprintName = sprintDetails?.data?.decryptedData?.name || "";
  const totalBugCount = sprintDetails?.data?.decryptedData?.totalBugCount || 0;
  const doneBugCount = sprintDetails?.data?.decryptedData?.doneBugCount || 0;
  const openWorkItems = totalBugCount - doneBugCount;

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setMoveToOption("new_sprint");

      // Set default selected sprint if no sprint prop is provided
      if (!sprint && activeSprints && activeSprints.length > 1) {
        setSelectedSprintId(activeSprints[0]._id);
      } else if (!sprint && activeSprints && activeSprints.length === 1) {
        setSelectedSprintId(activeSprints[0]._id);
      }
    }
  }, [open, sprint, activeSprints]);

  // Handle form submission
  const handleComplete = () => {
    const payload: SprintCompleteData = {
      sprintId: sprintToComplete,
      moveOpenItemsTo: openWorkItems === 0 ? "EXISTING_SPRINT" : moveToOption,
    };

    onComplete(payload);
  };

  // Generate move to options
  const getMoveToOptions = () => {
    const options = [
      { value: "new_sprint", label: t("New sprint") },
      { value: "backlog", label: t("Backlog") },
    ];

    // Add draft sprints
    draftSprints.forEach((draftSprint) => {
      options.push({
        value: draftSprint._id,
        label: draftSprint.name,
      });
    });

    return options;
  };

  const moveToOptions = getMoveToOptions();

  // Check if we need to show sprint selection
  const shouldShowSprintSelection =
    !sprint && activeSprints && activeSprints.length > 1;

  // Disable complete button if no sprint is selected when needed
  const isCompleteDisabled =
    isLoading || isLoadingSprintDetails || (!sprint && !sprintToComplete);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: 400,
        },
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Typography variant="h5" fontWeight={600}>
          {t("Complete")} {sprintName || t("Sprint")}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        {/* Sprint Selection - only show if no sprint prop and multiple active sprints */}
        {shouldShowSprintSelection && (
          <Box sx={{ my: 3 }}>
            <FormControl fullWidth>
              <InputLabel id="sprint-select-label">
                {t("Select a sprint")}
              </InputLabel>
              <StyledSelect
                labelId="sprint-select-label"
                value={selectedSprintId}
                onChange={(e) => setSelectedSprintId(e.target.value)}
                label={t("Select a sprint")}
                size="medium"
                sx={{
                  "& .MuiSelect-select": {
                    py: 1.5,
                  },
                }}
              >
                {activeSprints.map((activeSprint) => (
                  <MenuItem key={activeSprint._id} value={activeSprint._id}>
                    {activeSprint.name}
                  </MenuItem>
                ))}
              </StyledSelect>
            </FormControl>
          </Box>
        )}

        {isLoadingSprintDetails ? (
          <Box
            sx={{
              py: 3,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : sprintToComplete ? (
          <>
            {/* Sprint Summary */}
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
              {t("This sprint contains")}{" "}
              <strong>
                {doneBugCount} {t("completed work items")}
              </strong>{" "}
              {t("and")}{" "}
              <strong>
                {openWorkItems} {t("open work items")}
              </strong>
              .
            </Typography>

            {/* Information List */}
            <List sx={{ mb: 3, pl: 0 }}>
              <ListItem sx={{ px: 0, alignItems: "flex-start" }}>
                <Typography component="span" sx={{ mr: 1, mt: 0.5 }}>
                  •
                </Typography>
                <ListItemText
                  primary={
                    <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                      {t(
                        "Completed work items includes everything in the last column on the board, "
                      )}
                      <Typography
                        component="span"
                        sx={{ color: "primary.main", textDecoration: "none" }}
                      >
                        {t("Done")}
                      </Typography>
                      .
                    </Typography>
                  }
                />
              </ListItem>
              <ListItem sx={{ px: 0, alignItems: "flex-start" }}>
                <Typography component="span" sx={{ mr: 1, mt: 0.5 }}>
                  •
                </Typography>
                <ListItemText
                  primary={
                    <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                      {t(
                        "Open work items includes everything from any other column on the board. Move these to a new sprint or the backlog."
                      )}
                    </Typography>
                  }
                />
              </ListItem>
            </List>

            {/* Move Open Work Items Section */}
            {openWorkItems > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 2 }}>
                  {t("Move open work items to")}
                </Typography>
                <StyledSelect
                  value={moveToOption}
                  onChange={(e) => setMoveToOption(e.target.value)}
                  fullWidth
                  size="medium"
                  sx={{
                    "& .MuiSelect-select": {
                      py: 1.5,
                    },
                  }}
                >
                  {moveToOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </StyledSelect>
              </Box>
            )}
          </>
        ) : (
          // Show message when no sprint is available to complete
          !shouldShowSprintSelection && (
            <Box sx={{ py: 3, textAlign: "center" }}>
              <Typography variant="body1" color="text.secondary">
                {t("No active sprints available to complete.")}
              </Typography>
            </Box>
          )
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          size="large"
          disabled={isLoading}
          sx={{
            minWidth: 100,
            textTransform: "none",
          }}
        >
          {t("Cancel")}
        </Button>
        <Button
          onClick={handleComplete}
          variant="contained"
          size="large"
          disabled={isCompleteDisabled}
        >
          {isLoading ? t("Completing...") : t("Complete sprint")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SprintCompleteModal;
